import { useCallback, useEffect, useRef, useState } from 'react';
import { Tabs, TabsProps } from 'antd';
import classNames from 'classnames';

import { useAppSelector, useResizeLayout } from '@Hooks/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';

import { useGetEmpPhotoQuery } from '@Store/api/dictionaryApiSlice';
import { UserInfo } from './components/UserInfo';

import styles from './profile-main-section.module.css';

interface ProfileMainSectionProps {
  activeTabKey: string;
  tabsItems: TabsProps['items'];
  onTabsHeightCalculated: (height: number) => void;
  handleSelectActiveTab: (tabName: string) => void;
}

const ProfileMainSection: React.FC<ProfileMainSectionProps> = ({
  activeTabKey,
  tabsItems,
  onTabsHeightCalculated,
  handleSelectActiveTab,
}) => {
  const { isMobile } = useResizeLayout();

  const user = useAppSelector(currentUserSelector);
  const { data } = useGetEmpPhotoQuery(user.empImageName || '');

  const [style, setStyle] = useState<Record<string, string> | undefined>(
    undefined
  );
  const [isTabsSticky, setIsTabsSticky] = useState(false);

  const tabsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const tabsHeight = (tabsRef.current as HTMLDivElement).clientHeight;
    onTabsHeightCalculated(tabsHeight);
  }, [isTabsSticky, onTabsHeightCalculated]);

  const handleStickyChange = useCallback((isSticky: boolean): void => {
    setIsTabsSticky(isSticky);
  }, []);

  const handleUserInfoHeight = useCallback((height: number) => {
    // should be the same as value of css variable --profile-page-padding
    const PROFILE_PAGE_PADDING = 24;
    const topValue = height + PROFILE_PAGE_PADDING;

    setStyle({
      top: `-${topValue}px`,
    });
  }, []);

  return (
    <div
      className={classNames(styles.wrapper, isMobile ? 'mobile' : null)}
      style={style}
    >
      <UserInfo
        onStickyChange={handleStickyChange}
        onHeightCalculated={handleUserInfoHeight}
        name={user.empName}
        occupation={user.occupation || EMPTY_FIELD_PLACEHOLDER}
        photo={data}
        status={user.status}
      />
      <div ref={tabsRef}>
        <Tabs
          activeKey={activeTabKey}
          tabPosition="bottom"
          items={tabsItems}
          destroyInactiveTabPane
          className={classNames(
            styles.tabs,
            isTabsSticky ? styles.tabsSticky : null
          )}
          indicator={{ size: 16 }}
          onTabClick={handleSelectActiveTab}
        />
      </div>
    </div>
  );
};

export default ProfileMainSection;
